import { useEffect, useState } from 'react';
import Head from 'next/head';
import HeaderJp from '@/utils/pageParts/top/jp/Header';
import MenuJp from '@/utils/pageParts/top/jp/Menu';
import LeftMenuJp from '@/utils/pageParts/top/jp/LeftMenu';
import RightMenuJp from '@/utils/pageParts/top/jp/RightMenu';
import styles from '@/css/index.module.css';
import FooterJp from '@/utils/pageParts/top/jp/Footer';
import type {
    WikiCounter,
    WikiPage,
    LikedWiki
} from '@/utils/pageParts/top/indexInterfaces';
import LoginedUI from '@/utils/pageParts/top/jp/indexLogined';
import LogoutedUI from '@/utils/pageParts/top/jp/indexLogouted';
import {
    fetchRecentPages,
    fetchLikedWikis,
    fetched14ninstudioCounter
} from '@/utils/pageParts/top/jp/indexfetchs';
import { supabaseClient } from '@/lib/supabaseClient';
import { User } from '@supabase/auth-helpers-react';
import ClientErrorUI from '@/utils/pageParts/top/clienterrorUI';
import ImageContainer from '@/utils/pageParts/top/ImageContainer';

export interface ClientError {
    type: 'error' | 'promise';
    message: string;
    stack?: string;
}

const AsakuraWikiTitle = '無料 レンタル Wiki サービス あさクラWiki';

export default function Home() {
    /* ===============================
        state
    =============================== */
    const [pages, setPages] = useState<WikiPage[]>([]);
    const [recentPages, setRecentPages] = useState<WikiPage[]>([]);
    const [likedWikis, setLikedWikis] = useState<LikedWiki[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingLiked, setLoadingLiked] = useState(true);
    const [loadingRecent, setLoadingRecent] = useState(true);
    const [menuStatus, setMenuStatus] = useState(false);
    const [wiki14ninstudioCounter, setWiki14ninstudioCounter] =
        useState<WikiCounter | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [mounted, setMounted] = useState(false);
    const [clientError, setClientError] = useState<ClientError | null>(null);

    /* ===============================
        Bot 判定（state）
    =============================== */
    const [isBot, setIsBot] = useState(true);

    /* ===============================
        mount & bot detect
    =============================== */
    useEffect(() => {
        setMounted(true);

        if (typeof window === 'undefined') {
            setIsBot(true);
            return;
        }

        const ua = navigator.userAgent;
        const bot =
            /(Googlebot|Google-InspectionTool|AdsBot-Google|bingbot|Slurp|DuckDuckBot|YandexBot|Baiduspider|facebook\.com|HeadlessChrome)/i.test(ua);

        setIsBot(bot);

        console.log('[UA]', ua);
        console.log('[isBot]', bot);
    }, []);

    /* ===============================
        global error capture（人間のみ）
    =============================== */
    useEffect(() => {
        if (isBot) return;

        const onError = (event: ErrorEvent) => {
            console.error('[GLOBAL ERROR]', event);
            setClientError({
                type: 'error',
                message: event.message,
                stack: event.error?.stack
            });
        };

        const onUnhandledRejection = (event: PromiseRejectionEvent) => {
            console.error('[UNHANDLED PROMISE]', event.reason);
            setClientError({
                type: 'promise',
                message:
                    event.reason instanceof Error
                        ? event.reason.message
                        : String(event.reason),
                stack: event.reason?.stack
            });
        };

        window.addEventListener('error', onError);
        window.addEventListener(
            'unhandledrejection',
            onUnhandledRejection
        );

        return () => {
            window.removeEventListener('error', onError);
            window.removeEventListener(
                'unhandledrejection',
                onUnhandledRejection
            );
        };
    }, [isBot]);

    /* ===============================
        auth（人間のみ）
    =============================== */
    useEffect(() => {
        if (isBot) return;

        supabaseClient.auth
            .getUser()
            .then(({ data }) => {
                if (data?.user) setUser(data.user);
            })
            .catch(e => {
                console.error('[AUTH ERROR]', e);
                setClientError({
                    type: 'error',
                    message: 'Auth error',
                    stack: e instanceof Error ? e.stack : undefined
                });
            });
    }, [isBot]);

    /* ===============================
        data fetch（完全 Bot 回避）
    =============================== */
    useEffect(() => {
        if (isBot) return;
        fetchRecentPages(
            setLoadingRecent,
            setRecentPages,
            setPages,
            setLoading
        );
        fetchLikedWikis(setLoadingLiked, setLikedWikis);
        fetched14ninstudioCounter(setWiki14ninstudioCounter);
    }, [isBot]);

    /* ===============================
        wiki counter 使用（明示）
    =============================== */
    useEffect(() => {
        if (isBot) return;
        console.log('[counter]', wiki14ninstudioCounter);
    }, [wiki14ninstudioCounter, isBot]);

    const wiki14ninstudioCounterTotal =
        (wiki14ninstudioCounter?.total ?? 0) + 3695;

    if (!isBot && !isNaN(wiki14ninstudioCounterTotal)) {
        console.log(
            'ApplicationAllViewedCounter:',
            wiki14ninstudioCounterTotal
        );
    }

    /* ===============================
        theme（人間のみ）
    =============================== */
    useEffect(() => {
        if (isBot) return;

        const html = document.documentElement;
        if (!user) html.setAttribute('data-theme', 'dark');
        else html.removeAttribute('data-theme');
    }, [user, isBot]);

    /* ===============================
        body lock（人間のみ）
    =============================== */
    useEffect(() => {
        if (isBot) return;

        document.body.style.overflow = menuStatus ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuStatus, isBot]);

    const H2Styles: React.CSSProperties = {
        marginBlockStart: '0.83em',
        marginBlockEnd: '0.83em',
        marginInlineStart: '0px',
        marginInlineEnd: '0px',
        unicodeBidi: 'isolate',
        textAlign: 'center'
    };

    /* ===============================
        人間用 error UI
    =============================== */
    if (clientError) {
        return (
            <ClientErrorUI
                clientError={clientError}
            />
        );
    }

    return (
        <>
            <Head>
                <title>{AsakuraWikiTitle}</title>
                <meta name="description" content="なんと1周年!これまでを大切に、これからも進化し続けます。"/>
                <meta property="og:title" content={AsakuraWikiTitle} />
                <meta property="og:description" content={AsakuraWikiTitle} />
                <meta property="og:site_name" content={AsakuraWikiTitle} />
                <meta property="og:url" content={process.env.NEXT_PUBLIC_API_BASE_URL} />
                {mounted && !user && (
                    <link
                        rel="stylesheet"
                        href="https://sakitibi.github.io/static.asakurawiki.com/css/unlogined.static.css"
                    />
                )}
                <script
                    id="json-ld-website"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "WebSite",
                            "name": "AsakuraWiki",
                            "url": process.env.NEXT_PUBLIC_API_BASE_URL,
                        }),
                    }}
                ></script>
            </Head>

            <ImageContainer/>

            <MenuJp
                handleClick={() => setMenuStatus(v => !v)}
                menuStatus={menuStatus}
            />

            <div className={styles.contentsWrapper}>
                <HeaderJp handleClick={() => setMenuStatus(v => !v)} />

                <div className={styles.contents}>
                    <LeftMenuJp URL="/" />

                    <main style={{ padding: '2rem', flex: 1, color: 'white' }}>
                        {user && !isBot ? (
                            <LoginedUI
                                wiki14ninstudioCounter={
                                    wiki14ninstudioCounter
                                }
                                wiki14ninstudioCounterTotal={
                                    wiki14ninstudioCounterTotal
                                }
                                loadingLiked={loadingLiked}
                                loadingRecent={loadingRecent}
                                loading={loading}
                                likedWikis={likedWikis}
                                pages={pages}
                                recentPages={recentPages}
                                goCreateWiki={() =>
                                    (location.href =
                                        '/dashboard/create-wiki')
                                }
                                H2Styles={H2Styles}
                            />
                        ) : (
                            <LogoutedUI />
                        )}
                    </main>

                    <RightMenuJp />
                </div>

                <FooterJp />
            </div>
        </>
    );
}
